import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, UserPlus, UserMinus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { findUserByUsername, getUsers } from "@/lib/storage";
import {
    getFollowing,
    getFollowers,
    isFollowing,
    toggleFollow,
} from "@/lib/socialStore";
import { useSocialVersion } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";

/**
 * Shared screen that lists followers OR following for the current user
 * (`/profile/followers`, `/profile/following`) or any public profile
 * (`/u/:username/followers`, `/u/:username/following`).
 */
const FollowList = ({ kind }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { username } = useParams();
    const v = useSocialVersion();
    void v;

    const ownerUser = useMemo(
        () => (username ? findUserByUsername(username) : user),
        [username, user]
    );

    const userIds = useMemo(() => {
        if (!ownerUser) return [];
        return kind === "followers"
            ? getFollowers(ownerUser.id)
            : getFollowing(ownerUser.id);
    }, [ownerUser, kind, v]);

    const usersMap = useMemo(() => {
        const map = new Map();
        getUsers().forEach((u) => u && map.set(u.id, u));
        return map;
    }, [v]);

    const items = userIds
        .map((id) => usersMap.get(id))
        .filter(Boolean);

    if (!ownerUser) {
        return (
            <div className="screen">
                <Header onBack={() => navigate(-1)} title={kind === "followers" ? "Followers" : "Following"} />
                <div className="px-6 py-12 text-center text-[13px] text-muted-foreground">
                    Account not found.
                </div>
            </div>
        );
    }

    const titlePerson = ownerUser.id === user?.id ? "Your" : `@${ownerUser.username}'s`;

    return (
        <div className="screen" data-testid={`follow-list-${kind}`}>
            <Header
                onBack={() => navigate(-1)}
                title={`${titlePerson} ${kind === "followers" ? "Followers" : "Following"}`}
                count={items.length}
            />
            <div className="flex-1 overflow-y-auto no-scrollbar pb-8">
                {items.length === 0 ? (
                    <div className="px-6 py-12 text-center" data-testid={`follow-list-${kind}-empty`}>
                        <p className="text-[13.5px] text-muted-foreground">
                            {kind === "followers"
                                ? "No followers yet."
                                : "Not following anyone yet."}
                        </p>
                    </div>
                ) : (
                    <ul className="px-3 pt-2 space-y-1.5">
                        {items.map((u) => (
                            <FollowRow
                                key={u.id}
                                u={u}
                                me={user}
                                onOpen={() => navigate(`/u/${u.username}`)}
                                testId={`follow-row-${u.id}`}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

const Header = ({ onBack, title, count }) => (
    <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between bg-background">
        <button
            onClick={onBack}
            data-testid="follow-list-back"
            className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
            aria-label="Back"
        >
            <ArrowLeft size={20} />
        </button>
        <h1 className="font-display text-[15px] font-semibold truncate max-w-[60%] text-center">
            {title}
            {typeof count === "number" && (
                <span className="ml-1.5 text-[12px] font-normal text-muted-foreground">
                    ({count})
                </span>
            )}
        </h1>
        <span className="h-10 w-10" />
    </header>
);

const FollowRow = ({ u, me, onOpen, testId }) => {
    const verified = !!u.verified;
    const isMe = me?.id === u.id;
    const following = !isMe && me ? isFollowing(me.id, u.id) : false;

    return (
        <li
            data-testid={testId}
            className="flex items-center gap-3 p-2.5 rounded-2xl border border-border bg-card hover:bg-accent/40 transition-colors"
        >
            <button
                type="button"
                onClick={onOpen}
                className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.99] transition-transform"
                data-testid={`${testId}-open`}
            >
                {u.avatar ? (
                    <img src={u.avatar} alt={u.fullName} className="h-12 w-12 rounded-full object-cover ring-1 ring-border" />
                ) : (
                    <InitialAvatar name={u.fullName || u.username} size={48} />
                )}
                <span className="min-w-0 flex-1">
                    <span className="block font-display text-[14px] font-semibold truncate inline-flex items-center gap-1">
                        {u.fullName || u.username}
                        {verified && <BadgeCheck size={14} className="text-primary shrink-0" />}
                    </span>
                    <span className="block text-[12px] text-muted-foreground truncate">
                        @{u.username}
                    </span>
                </span>
            </button>
            {!isMe && me && (
                <button
                    type="button"
                    data-testid={`${testId}-toggle`}
                    onClick={() => toggleFollow(me.id, u.id)}
                    className={[
                        "h-9 px-3 rounded-full inline-flex items-center gap-1 text-[12px] font-semibold transition-colors active:scale-[0.97]",
                        following
                            ? "bg-secondary text-secondary-foreground border border-border"
                            : "bg-primary text-primary-foreground",
                    ].join(" ")}
                >
                    {following ? <UserMinus size={12} /> : <UserPlus size={12} />}
                    {following ? "Following" : "Follow"}
                </button>
            )}
        </li>
    );
};

export default FollowList;
